// src/components/common/StudentSelectionModal.jsx
import React, { useState } from 'react';
import { X, User, UserPlus, Mail, Phone, Loader } from 'lucide-react';
import studentsService from '../../services/studentsService';

/**
 * StudentSelectionModal Component
 * Shows before enrollment form - asks if enrolling new or returning student
 * If returning, prompts for email or phone and shows student selection
 */
const StudentSelectionModal = ({ isOpen, onClose, onStudentSelected }) => {
  const [step, setStep] = useState('choice'); // 'choice', 'contact', 'select'
  const [searchType, setSearchType] = useState('email'); // 'email' or 'phone'
  const [contactValue, setContactValue] = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleNewStudent = () => {
    // User wants to enroll a new student
    onStudentSelected(null);
    onClose();
  };

  const handleReturningStudent = () => {
    // Move to contact entry step
    setStep('contact');
    setError('');
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Use public endpoint (isPublic: true) since this is for public enrollment
      const fetchedStudents = searchType === 'email'
        ? await studentsService.getStudentsByEmail(contactValue, true)
        : await studentsService.getStudentsByPhone(contactValue, true);

      if (fetchedStudents.length === 0) {
        const fieldName = searchType === 'email' ? 'email address' : 'phone number';
        setError(`No students found with this ${fieldName}. Please check the ${fieldName} or enroll as a new student.`);
        setLoading(false);
        return;
      }

      setStudents(fetchedStudents);
      setStep('select');
    } catch (err) {
      setError('Error searching for students. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentSelection = (student) => {
    onStudentSelected(student);
    onClose();
  };

  const handleBack = () => {
    if (step === 'contact') {
      setStep('choice');
      setContactValue('');
      setError('');
    } else if (step === 'select') {
      setStep('contact');
      setStudents([]);
      setError('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {step === 'choice' && 'Enroll a Student'}
            {step === 'email' && 'Returning Student'}
            {step === 'select' && 'Select Student'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: New or Returning Choice */}
          {step === 'choice' && (
            <div className="space-y-4">
              <p className="text-gray-600 mb-6">
                Are you enrolling a new student or a returning student?
              </p>

              <button
                onClick={handleNewStudent}
                className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center flex-shrink-0">
                  <UserPlus className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900 group-hover:text-blue-600">
                    New Student
                  </div>
                  <div className="text-sm text-gray-500">
                    First time enrolling with us
                  </div>
                </div>
              </button>

              <button
                onClick={handleReturningStudent}
                className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-green-100 group-hover:bg-green-200 flex items-center justify-center flex-shrink-0">
                  <User className="w-6 h-6 text-green-600" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900 group-hover:text-green-600">
                    Returning Student
                  </div>
                  <div className="text-sm text-gray-500">
                    Previously enrolled with us
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Step 2: Contact Entry (Email or Phone) */}
          {step === 'contact' && (
            <div className="space-y-4">
              <p className="text-gray-600 mb-4">
                Enter the {searchType === 'email' ? 'email address' : 'phone number'} you used when previously enrolling your student.
              </p>

              {/* Search Type Toggle */}
              <div className="flex gap-2 p-1 bg-gray-100 rounded-lg mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setSearchType('email');
                    setContactValue('');
                    setError('');
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-all ${
                    searchType === 'email'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Mail className="w-4 h-4" />
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSearchType('phone');
                    setContactValue('');
                    setError('');
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-all ${
                    searchType === 'phone'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Phone className="w-4 h-4" />
                  Phone
                </button>
              </div>

              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {searchType === 'email' ? 'Parent/Guardian Email' : 'Parent/Guardian Phone Number'}
                  </label>
                  <div className="relative">
                    {searchType === 'email' ? (
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    ) : (
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    )}
                    <input
                      type={searchType === 'email' ? 'email' : 'tel'}
                      value={contactValue}
                      onChange={(e) => setContactValue(e.target.value)}
                      placeholder={searchType === 'email' ? 'parent@example.com' : '(555) 123-4567'}
                      required
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      'Find Students'
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Step 3: Student Selection */}
          {step === 'select' && (
            <div className="space-y-4">
              <p className="text-gray-600 mb-4">
                We found {students.length} student{students.length !== 1 ? 's' : ''} associated with{' '}
                <strong>{contactValue}</strong>
              </p>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {students.map((student) => (
                  <button
                    key={student.id}
                    onClick={() => handleStudentSelection(student)}
                    className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group text-left"
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center flex-shrink-0 text-white font-semibold">
                      {student.studentFirstName?.charAt(0)}{student.studentLastName?.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 group-hover:text-blue-600">
                        {student.studentFirstName} {student.studentLastName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {student.age && `Age ${student.age}`}
                        {student.enrollmentCount > 0 && ` • ${student.enrollmentCount} active enrollment${student.enrollmentCount !== 1 ? 's' : ''}`}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <button
                onClick={handleBack}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentSelectionModal;
